const syncMegaPanelOffset = (item, trigger, panel) => {
  const triggerRect = trigger.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const offset = triggerRect.left - itemRect.left - 24;
  panel.style.setProperty('--mega-panel-offset', `${Math.max(0, Math.round(offset))}px`);
};

const DROPDOWN_CLOSE_MS = 160;

const closeOpenHeaderMenus = (header, except = null) => {
  header.querySelectorAll('.header__menu-item--has-dropdown.is-open, .header__menu-item--has-mega.is-open').forEach((item) => {
    if (item === except) return;
    item.classList.remove('is-open');
    const trigger = item.querySelector('.js-header-mega-trigger');
    const panel = item.querySelector('.js-header-mega-panel');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.setAttribute('aria-hidden', 'true');
      panel.toggleAttribute('inert', true);
    }
  });
};

const initDesktopDropdowns = (header) => {
  const items = [...header.querySelectorAll('.header__menu-item--has-dropdown:not(.header__menu-item--has-mega)')];
  if (!items.length) return;

  items.forEach((item) => {
    let closeTimer = null;
    const submenu = item.querySelector('.header__sub-menu');

    const open = () => {
      clearTimeout(closeTimer);
      closeOpenHeaderMenus(header, item);
      item.classList.add('is-open');
    };

    const scheduleClose = (event) => {
      if (event?.relatedTarget instanceof Node && item.contains(event.relatedTarget)) {
        return;
      }
      clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        item.classList.remove('is-open');
      }, DROPDOWN_CLOSE_MS);
    };

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);
    submenu?.addEventListener('mouseenter', open);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (event) => {
      if (item.contains(event.relatedTarget)) return;
      scheduleClose(event);
    });
  });
};

const initDesktopMegaMenu = (header) => {
  const items = [...header.querySelectorAll('.js-header-mega')];
  if (!items.length) return;

  const setPanelState = (item, trigger, panel, open) => {
    item.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
    panel.setAttribute('aria-hidden', String(!open));
    panel.toggleAttribute('inert', !open);
  };

  const closeAll = () => {
    closeOpenHeaderMenus(header);
  };

  items.forEach((item) => {
    const trigger = item.querySelector('.js-header-mega-trigger');
    const panel = item.querySelector('.js-header-mega-panel');
    if (!trigger || !panel) return;

    panel.toggleAttribute('inert', true);

    let closeTimer = null;

    const open = () => {
      clearTimeout(closeTimer);
      closeOpenHeaderMenus(header, item);
      syncMegaPanelOffset(item, trigger, panel);
      setPanelState(item, trigger, panel, true);
    };

    const close = () => {
      clearTimeout(closeTimer);
      setPanelState(item, trigger, panel, false);
    };

    const scheduleClose = (event) => {
      if (event?.relatedTarget instanceof Node && item.contains(event.relatedTarget)) {
        return;
      }
      clearTimeout(closeTimer);
      closeTimer = window.setTimeout(close, DROPDOWN_CLOSE_MS);
    };

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);
    // Tylko cancel — zamykanie wyłącznie z item.mouseleave (panel.mouseleave
    // przy powrocie do triggera zamykał menu mimo że kursor dalej był w li).
    panel.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
    });

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (item.classList.contains('is-open')) close();
      else open();
    });

    trigger.addEventListener('focus', open);

    window.addEventListener('resize', () => {
      if (item.classList.contains('is-open')) {
        syncMegaPanelOffset(item, trigger, panel);
      }
    }, { passive: true });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAll();
  });
};

const resetMobileSchool = (wrapper) => {
  const school = wrapper.querySelector('.js-mobile-school');
  if (!school) return;

  school.classList.remove('is-open');
  school.querySelector('.js-mobile-school-toggle')?.setAttribute('aria-expanded', 'false');

  wrapper.querySelectorAll('.js-mobile-age').forEach((age) => {
    age.classList.remove('is-open');
    age.querySelector('.js-mobile-age-toggle')?.setAttribute('aria-expanded', 'false');
  });
};

const resetMobileAccordions = (wrapper) => {
  resetMobileSchool(wrapper);
  wrapper.querySelectorAll('.js-mobile-accordion.is-open').forEach((item) => {
    item.classList.remove('is-open');
    item.querySelector('.js-mobile-accordion-toggle')?.setAttribute('aria-expanded', 'false');
  });
};

const MOBILE_PANEL_MS = 300;

/** Drawer rezerwacji (Klub Pingwina / oferta) ma z-index ~10060 — zamknij przy otwarciu menu. */
const closeOfferDrawers = () => {
  document.dispatchEvent(new CustomEvent('hski:close-offer-drawers'));

  document.querySelectorAll('.offer-page__sidebar-drawer').forEach((drawer) => {
    if (!(drawer instanceof HTMLElement)) return;
    if (!drawer.classList.contains('is-open') && drawer.hidden) return;

    const panel = drawer.querySelector('.offer-page__sidebar-drawer-panel');
    const backdrop = drawer.querySelector('.offer-page__sidebar-drawer-backdrop');
    panel?.classList.remove('is-dragging');
    if (panel instanceof HTMLElement) panel.style.transform = '';
    if (backdrop instanceof HTMLElement) backdrop.style.opacity = '';

    drawer.classList.remove('is-open');
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
  });

  document.body.classList.remove('is-offer-sidebar-drawer-open');
  document.querySelectorAll('.is-sidebar-drawer-open').forEach((root) => {
    root.classList.remove('is-sidebar-drawer-open');
  });
};

const mobileMenu = () => {
  const burger = document.querySelector('.js-hamburger-open');
  const mobileWrapper = document.querySelector('.js-mobile-wrapper');
  if (!burger || !mobileWrapper) return;
  // app.js bywa ładowany 2× (WP ?ver= + import z chunka) — drugi bind = open zaraz close.
  if (burger.dataset.hskiMenuBound === '1') return;
  burger.dataset.hskiMenuBound = '1';

  let closeTimer = null;

  const finishClose = () => {
    closeTimer = null;
    mobileWrapper.classList.remove('is-closing', 'nav-visible');
    mobileWrapper.setAttribute('aria-hidden', 'true');
    burger.classList.remove('active');
    burger.setAttribute('aria-expanded', 'false');
    burger.setAttribute('aria-label', 'Otwórz menu');
    document.body.classList.remove('overflow-hidden', 'mobile-menu-open');
    resetMobileAccordions(mobileWrapper);
  };

  const closeMenu = () => {
    if (!mobileWrapper.classList.contains('active') || mobileWrapper.classList.contains('is-closing')) {
      return;
    }

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    mobileWrapper.classList.remove('active');
    mobileWrapper.classList.add('is-closing');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      finishClose();
      return;
    }

    closeTimer = window.setTimeout(finishClose, MOBILE_PANEL_MS);
  };

  const openMenu = () => {
    if (mobileWrapper.classList.contains('active') || mobileWrapper.classList.contains('is-closing')) {
      return;
    }

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    closeOfferDrawers();

    mobileWrapper.classList.remove('is-closing');
    burger.classList.add('active');
    burger.setAttribute('aria-expanded', 'true');
    burger.setAttribute('aria-label', 'Zamknij menu');
    mobileWrapper.classList.add('active', 'nav-visible');
    mobileWrapper.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden', 'mobile-menu-open');
    document.body.classList.remove('nav-hide');
    document.dispatchEvent(new CustomEvent('hski:nav-visibility'));
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && mobileWrapper.classList.contains('active')) {
      closeMenu();
      burger.focus();
    }
  });

  mobileWrapper.addEventListener('click', (event) => {
    const accordionToggle = event.target.closest('.js-mobile-accordion-toggle');
    if (accordionToggle) {
      event.preventDefault();
      event.stopPropagation();

      const item = accordionToggle.closest('.js-mobile-accordion');
      if (!item) return;

      const willOpen = !item.classList.contains('is-open');
      item.classList.toggle('is-open', willOpen);
      accordionToggle.setAttribute('aria-expanded', String(willOpen));

      if (willOpen) {
        resetMobileSchool(mobileWrapper);
      }
      return;
    }

    const schoolToggle = event.target.closest('.js-mobile-school-toggle');
    if (schoolToggle) {
      event.preventDefault();
      event.stopPropagation();

      const school = schoolToggle.closest('.js-mobile-school');
      if (!school) return;

      const willOpen = !school.classList.contains('is-open');
      if (!willOpen) {
        resetMobileSchool(mobileWrapper);
        return;
      }

      mobileWrapper.querySelectorAll('.js-mobile-accordion.is-open').forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('.js-mobile-accordion-toggle')?.setAttribute('aria-expanded', 'false');
      });

      school.classList.add('is-open');
      schoolToggle.setAttribute('aria-expanded', 'true');
      return;
    }

    const ageToggle = event.target.closest('.js-mobile-age-toggle');
    if (ageToggle) {
      event.preventDefault();
      event.stopPropagation();

      const age = ageToggle.closest('.js-mobile-age');
      if (!age) return;

      const willOpen = !age.classList.contains('is-open');

      mobileWrapper.querySelectorAll('.js-mobile-age.is-open').forEach((openAge) => {
        if (openAge === age) return;
        openAge.classList.remove('is-open');
        openAge.querySelector('.js-mobile-age-toggle')?.setAttribute('aria-expanded', 'false');
      });

      age.classList.toggle('is-open', willOpen);
      ageToggle.setAttribute('aria-expanded', String(willOpen));
      return;
    }

    if (event.target.closest('a')) closeMenu();
  });

  burger.addEventListener('click', (event) => {
    event.stopPropagation();
    if (mobileWrapper.classList.contains('active')) {
      closeMenu();
      return;
    }

    openMenu();
  });

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      mobileWrapper.classList.add('is-ready');
    });
  });
};

const syncSearchBarHeight = (header, { open } = {}) => {
  const panel = header.querySelector('.js-header-search');
  if (!panel) {
    document.documentElement.style.setProperty('--search-bar-height', '0px');
    return;
  }

  const isOpen = open ?? (document.body.classList.contains('header-search-open') && panel.classList.contains('is-open'));
  if (!isOpen) {
    document.documentElement.style.setProperty('--search-bar-height', '0px');
    return;
  }

  const openHeight = getComputedStyle(document.documentElement)
    .getPropertyValue('--search-bar-open-height')
    .trim() || '72px';
  document.documentElement.style.setProperty('--search-bar-height', openHeight);
};

const initHeaderSearch = (header) => {
  const openBtns = [...header.querySelectorAll('.js-header-search-open')];
  const panel = header.querySelector('.js-header-search');
  const closeBtn = header.querySelector('.js-header-search-close');
  const input = header.querySelector('.js-header-search-input');
  if (!openBtns.length || !panel) return;

  const setOpen = (open) => {
    document.body.classList.toggle('header-search-open', open);
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    openBtns.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
    syncSearchBarHeight(header, { open });

    if (open) {
      document.body.classList.remove('nav-hide');
      document.dispatchEvent(new CustomEvent('hski:nav-visibility'));
      window.requestAnimationFrame(() => {
        input?.focus();
      });
    }
  };

  openBtns.forEach((openBtn) => {
    openBtn.addEventListener('click', () => {
      setOpen(!panel.classList.contains('is-open'));
    });
  });

  closeBtn?.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('header-search-open')) {
      setOpen(false);
      const visibleBtn = openBtns.find((btn) => btn.getClientRects().length > 0) || openBtns[0];
      visibleBtn?.focus();
    }
  });

  syncSearchBarHeight(header, { open: false });
};

const initHeaderScroll = (header) => {
  const inner = header.querySelector('.header__inner') || header;
  let lastNavH = -1;
  let lastScroll = 0;
  let lastNavHide = document.body.classList.contains('nav-hide');

  // Odczyt offsetHeight = wymuszony reflow. Nie czytamy na każdym scrollu —
  // wysokość paska zmienia się tylko przy resize / zmianie layoutu (ResizeObserver).
  const applyNavHeight = (navH) => {
    if (navH === lastNavH) return;
    lastNavH = navH;
    document.documentElement.style.setProperty('--nav-height-js', `${navH}px`);
    syncSearchBarHeight(header);
  };

  const measureNavHeight = () => {
    applyNavHeight(inner.offsetHeight);
  };

  const setNavHide = (hide) => {
    document.body.classList.toggle('nav-hide', hide);
    if (hide === lastNavHide) return;
    lastNavHide = hide;
    document.dispatchEvent(new CustomEvent('hski:nav-visibility'));
  };

  /** Ukrywanie paska przy scrollu w dół — tylko mobile/tablet. */
  const isMobileNavHide = () => window.matchMedia('(max-width: 1024px)').matches;

  /** Body class z PHP: gdy true — chowanie przy scrollu (obecnie wyłączone → navbar static). */
  const allowsNavAutoHide = () => document.body.classList.contains('hski-nav-auto-hide');

  const scrollEvent = (currentScroll) => {
    if (!isMobileNavHide()) {
      document.body.classList.remove('sticky', 'nav-hide');
      setNavHide(false);
      lastScroll = currentScroll;
      return;
    }

    if (!allowsNavAutoHide()) {
      document.body.classList.remove('nav-hide');
      setNavHide(false);
      if (currentScroll > 100) {
        document.body.classList.add('sticky');
      } else {
        document.body.classList.remove('sticky');
      }
      lastScroll = currentScroll;
      return;
    }

    if (
      document.body.classList.contains('header-search-open')
      || document.body.classList.contains('mobile-menu-open')
    ) {
      setNavHide(false);
      if (currentScroll > 100) {
        document.body.classList.add('sticky');
      } else {
        document.body.classList.remove('sticky');
      }
      lastScroll = currentScroll;
      return;
    }

    if (currentScroll > 100) {
      document.body.classList.add('sticky');
    } else {
      document.body.classList.remove('sticky');
      setNavHide(false);
    }

    if (currentScroll > 0 && lastScroll <= currentScroll) {
      if (document.body.classList.contains('sticky')) {
        setNavHide(true);
      }
    } else {
      setNavHide(false);
    }

    lastScroll = currentScroll;
  };

  measureNavHeight();
  scrollEvent(window.scrollY || 0);

  window.addEventListener('scroll', () => {
    scrollEvent(window.scrollY || 0);
  }, { passive: true });

  window.addEventListener('resize', () => {
    if (!isMobileNavHide()) {
      document.body.classList.remove('sticky');
      setNavHide(false);
    }
  }, { passive: true });

  if (typeof ResizeObserver === 'function') {
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const boxH = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect?.height;
      if (typeof boxH === 'number' && boxH > 0) {
        applyNavHeight(Math.round(boxH));
      } else {
        measureNavHeight();
      }
    });
    ro.observe(inner);
    return;
  }

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measureNavHeight, 100);
  }, { passive: true });
};

const Header = async () => {
  const header = document.querySelector('.header');
  if (!header) return;

  mobileMenu();
  initDesktopDropdowns(header);
  initDesktopMegaMenu(header);
  initHeaderSearch(header);
  initHeaderScroll(header);

  // Temporary Google Translate until real Polylang EN (only when .js-gt-lang present).
  if (
    document.documentElement.dataset.hskiI18n !== 'pll'
    && document.querySelector('.js-gt-lang')
  ) {
    const { default: googleTranslateTemp } = await import('../../../js/googleTranslateTemp.js');
    googleTranslateTemp();
  }
};

const activeSection = () => {
  const sections = document.querySelectorAll("[id^='section-']");
  const navLinks = [...document.querySelectorAll('.nav__bottom a, .mobile-menu__bottom a')]
    .filter((link) => !link.closest('.offer-sub-nav, .js-offer-sub-nav'));

  if (!sections.length || !navLinks.length) return;

  const observerCallback = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const sectionId = entry.target.id;

      navLinks.forEach((link) => {
        const href = link.getAttribute('href')?.substring(1);
        link.classList.toggle('active', href === sectionId);
      });
    });
  };

  const observer = new IntersectionObserver(observerCallback, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  });

  sections.forEach((section) => observer.observe(section));
};

export { activeSection };
export default Header;
